
import Vue from 'vue'

type FieldKey = 'name' | 'tel' | 'email'

type TelRegion = 'HK' | 'CN' | 'MO' | 'TW' | 'SG' | 'MY' | 'UK' | 'JP' | 'US'

export default Vue.extend({
  props: {
    isAlert: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: 'default',
    },
  },
  data() {
    return {
      name: '',
      email: '',
      telRegion: 'HK' as TelRegion,
      telNumber: '',
      isRegionOpen: false,
      loading: false,
      touched: {
        name: false,
        tel: false,
        email: false,
      } as Record<FieldKey, boolean>,
      errors: {
        name: '',
        tel: '',
        email: '',
      } as Record<FieldKey, string>,
    }
  },
  computed: {
    variantClass(): Record<string, boolean> {
      const variant = this.variant as 'default' | 'compact' | 'contact-block'
      return {
        'is-compact': variant === 'compact',
        'is-contact-block': variant === 'contact-block',
      }
    },
    telRegionOptions() {
      return [
        { value: 'HK' as const, label: '香港（中國） +852' },
        { value: 'CN' as const, label: '大陸 +86' },
        { value: 'MO' as const, label: '澳門（中國） +853' },
        { value: 'TW' as const, label: '臺灣（中國） +886' },
        { value: 'SG' as const, label: '新加坡 +65' },
        { value: 'MY' as const, label: '馬來西亞 +60' },
        { value: 'UK' as const, label: '英國 +44' },
        { value: 'JP' as const, label: '日本 +81' },
        { value: 'US' as const, label: '美國 +1' },
      ]
    },
    telDialCode(): string {
      const map: Record<TelRegion, string> = {
        HK: '+852',
        CN: '+86',
        MO: '+853',
        TW: '+886',
        SG: '+65',
        MY: '+60',
        UK: '+44',
        JP: '+81',
        US: '+1',
      }
      return map[this.telRegion as TelRegion]
    },
    telDialCodeNoPlus(): string {
      return (this.telDialCode as string).replace(/^\+/, '')
    },
    currentRegionLabel(): string {
      const region = this.telRegion as TelRegion
      const found = (this.telRegionOptions as Array<{ value: TelRegion; label: string }>).find(
        (x) => x.value === region
      )
      return found ? found.label : this.telDialCode
    },
    formattedTel(): string {
      const digits = this.normalizeDigits(this.telNumber as string)
      return `${this.telDialCodeNoPlus}-${digits}`
    },
  },
  methods: {
    normalizeDigits(raw: string) {
      return (raw || '').replace(/[^\d]/g, '')
    },
    validateName(): string {
      if (!this.name) return '請輸入稱呼'
      return ''
    },
    validateEmail(): string {
      if (!this.email) return '請輸入電子郵件'
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!re.test(this.email)) return '電子郵件格式不正確'
      return ''
    },
    validateTel(): string {
      const digits = this.normalizeDigits(this.telNumber as string)
      if (!digits) return '請輸入手機號碼'

      const rules: Record<TelRegion, RegExp> = {
        HK: /^(4|5|6|8|9)\d{7}$/,
        CN: /^1[3-9]\d{9}$/,
        MO: /^(6|9)\d{7}$/,
        TW: /^(09|9)\d{8}$/,
        SG: /^[89]\d{7}$/,
        MY: /^1\d{8,9}$/,
        UK: /^(07|7)\d{9}$/,
        JP: /^(70|80|90)\d{8}$/,
        US: /^\d{10}$/,
      }

      const region = this.telRegion as TelRegion
      if (!rules[region].test(digits)) return '手機號碼格式不正確'
      return ''
    },
    validateField(field: FieldKey) {
      if (field === 'name') this.errors.name = this.validateName()
      if (field === 'tel') this.errors.tel = this.validateTel()
      if (field === 'email') this.errors.email = this.validateEmail()
    },
    validateAll() {
      this.errors.name = this.validateName()
      this.errors.tel = this.validateTel()
      this.errors.email = this.validateEmail()
      return !(this.errors.name || this.errors.tel || this.errors.email)
    },
    onBlur(field: FieldKey) {
      this.touched[field] = true
      this.validateField(field)
    },
    toggleRegionMenu() {
      this.isRegionOpen = !this.isRegionOpen
      if (!this.isRegionOpen) this.onBlur('tel')
    },
    closeRegionMenu() {
      if (!this.isRegionOpen) return
      this.isRegionOpen = false
      this.onBlur('tel')
    },
    selectRegion(region: TelRegion) {
      this.telRegion = region
      this.closeRegionMenu()
    },
    onDocumentClick(e: MouseEvent) {
      const wrap = this.$refs.telPrefixWrap as HTMLElement | undefined
      const target = e.target as Node | null
      if (!wrap || !target) return
      if (!wrap.contains(target)) this.closeRegionMenu()
    },
    focusFirstError() {
      if (this.errors.name) {
        const el = this.$refs.name as HTMLInputElement | undefined
        el?.focus?.()
        return
      }
      if (this.errors.tel) {
        const el = this.$refs.tel as HTMLInputElement | undefined
        el?.focus?.()
        return
      }
      if (this.errors.email) {
        const el = this.$refs.email as HTMLInputElement | undefined
        el?.focus?.()
        return
      }
    },
    submitForm() {
      this.touched.name = this.touched.tel = this.touched.email = true

      if (!this.validateAll()) {
        this.focusFirstError()
        return false
      }

      const email = encodeURIComponent(this.email as string)
      const tel = encodeURIComponent(this.formattedTel as string)
      const name = encodeURIComponent(this.name as string)

      // console.log(`https://meal.pin2eat.com/send-consult-email?email=${email}&tel=${tel}&name=${name}`)

      const xhr = new XMLHttpRequest()
      xhr.open(
        'get',
        `https://meal.pin2eat.com/send-consult-email?email=${email}&tel=${tel}&name=${name}`
      )
      xhr.send()
      this.loading = true
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const res = JSON.parse(xhr.response)
              if (res && res.code === 200) {
                this.email = ''
                this.telNumber = ''
                this.telRegion = 'HK'
                this.name = ''
                this.errors = { name: '', tel: '', email: '' }
                this.touched = { name: false, tel: false, email: false }
                if (this.isAlert) alert('提交成功')
                this.$emit('submit-success')
                this.loading = false
                return
              }
              alert('提交失敗，請稍後重試')
            } catch {
              alert('提交失敗，請刷新後重試')
            }
          } else {
            alert('提交失敗，請稍後重試')
          }
        }
        this.loading = false
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
  },
})
